import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";

import { FileStatus } from "@/Features/Files/files.types";

import { storagePhotosQueryKeys } from "./storage-photos.keys";
import { StoragePhotosAPI } from "./storage-photos.api";
import type {
  DeleteStoragePhotoVariables,
  StoragePhoto,
} from "./storage-photos.types";

// The API caps page_size at 1000 (STORAGE_PHOTO_MAX_PAGE_SIZE); 500 stays under it.
const PAGE_SIZE = 500;
// Signed `link`s last 1 day (getFiles); stay well under that.
const STALE_TIME = 1000 * 60 * 30;

/**
 * Fetches every page of an item's photos, always with `include_pending=true`
 * so server `uploading` rows come back, following `nextPage` until null.
 */
export async function fetchAllStoragePhotos(
  uuid: string
): Promise<StoragePhoto[]> {
  const photos: StoragePhoto[] = [];
  let page: number | null = 1;
  while (page != null) {
    const res = await StoragePhotosAPI.get(uuid, {
      page,
      page_size: PAGE_SIZE,
      include_pending: true,
    });
    // The API layer rejects { success: false } envelopes; guard the shape
    // anyway so an error object is never cached as data.
    if (!Array.isArray(res.data)) {
      throw new Error(`Failed to fetch photos for storage item ${uuid}`);
    }
    photos.push(...res.data);
    // A repeated or regressed page would loop forever; fail loudly rather
    // than silently truncating the list.
    if (res.nextPage != null && res.nextPage <= page) {
      throw new Error(
        `Photo list for storage item ${uuid} returned nextPage ${res.nextPage} after page ${page}`
      );
    }
    page = res.nextPage;
  }
  return photos;
}

/**
 * Every photo of one storage item, keyed by the numeric `storage_id` (see
 * `storagePhotosQueryKeys.all` invalidation reaches it) and fetched by `uuid`.
 *
 * See fetchAllStoragePhotos for paging and `include_pending`.
 */
export function useGetStoragePhotos(
  { storage_id, uuid }: { storage_id?: number; uuid?: string },
  options?: { enabled?: boolean }
) {
  return useQuery<StoragePhoto[]>({
    queryKey: storagePhotosQueryKeys.list(storage_id),
    queryFn: () => fetchAllStoragePhotos(uuid as string),
    enabled: storage_id != null && !!uuid && (options?.enabled ?? true),
    // Failed requests already toast via the connection.js interceptor, once
    // per attempt — don't retry.
    retry: false,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: true,
  });
}

export function useDeleteStoragePhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ storage_uuid, photo_uuid }: DeleteStoragePhotoVariables) =>
      StoragePhotosAPI.delete({ storage_uuid, photo_uuid }),
    // No optimistic removal: the photo stays in the grid, and the delete modal
    // stays in its submitting state, until the API call has completed. A failed
    // delete then needs no rollback, because nothing was removed.
    onSuccess: async (_data, { storage_id, photo_uuid }) => {
      const queryKey = storagePhotosQueryKeys.list(storage_id);
      // an in-flight list fetch could otherwise put the deleted row back
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData<StoragePhoto[]>(queryKey, (existing) =>
        existing?.filter((p) => p.uuid !== photo_uuid)
      );
      queryClient.invalidateQueries({ queryKey });
    },
  });
}

/**
 * Adds (or replaces, by uuid) a freshly uploaded photo in the cached list.
 * No-op when the list has not been fetched; the next fetch will include it.
 *
 * A new photo goes first: the API lists newest first, so this is where a
 * refetch puts it, and the grid shows rows in cache order.
 */
export function appendStoragePhotoToCache(
  queryClient: QueryClient,
  storage_id: number,
  file: StoragePhoto
) {
  queryClient.setQueryData<StoragePhoto[]>(
    storagePhotosQueryKeys.list(storage_id),
    (existing) => {
      if (!existing) return existing;
      return existing.some((p) => p.uuid === file.uuid)
        ? existing.map((p) => (p.uuid === file.uuid ? file : p))
        : [file, ...existing];
    }
  );
}

/**
 * Applies a finished upload to the cached list.
 *
 * An in flight list fetch can still carry the photo as `uploading`; cancel it
 * before writing so it cannot put the stale row back and leave a stuck
 * "Uploading..." tile. The append is a no-op until the list has loaded, so
 * refetch in that case (or when the result carries no photo).
 */
export async function applyUploadedStoragePhoto(
  queryClient: QueryClient,
  storage_id: number,
  photo?: StoragePhoto
) {
  const queryKey = storagePhotosQueryKeys.list(storage_id);
  if (photo && queryClient.getQueryData(queryKey)) {
    await queryClient.cancelQueries({ queryKey });
    appendStoragePhotoToCache(queryClient, storage_id, photo);
  } else {
    await queryClient.invalidateQueries({ queryKey });
  }
}

export type PhotoEntry<Q extends { uuid: string; phase?: string }> = {
  uuid: string;
  /** Server row, absent while the upload is only known locally. */
  file?: StoragePhoto;
  /** Local queue item, present while it should drive progress and errors. */
  upload?: Q;
};

/**
 * Merges server rows (including `uploading` ones) with the local upload queue
 * by uuid; the queue and the server share the client-minted uuid.
 *
 * - Row with a queue item: both, so the queue drives live progress.
 * - Row whose queue item has completed: the row alone (the `active` row
 *   replaces the finished item).
 * - Server `uploading` row without a queue item: row alone (placeholder).
 * - Queue item without a server row yet: the item alone.
 */
export function reconcilePendingPhotos<
  Q extends { uuid: string; phase?: string }
>(serverRows: StoragePhoto[], queueItems: Q[]): PhotoEntry<Q>[] {
  const queueByUuid = new Map(queueItems.map((q) => [q.uuid, q]));
  const seen = new Set<string>();
  const entries: PhotoEntry<Q>[] = [];

  // Paging a changing list can return a uuid twice (uploading, then active);
  // keep the first position but prefer the ACTIVE row.
  const rowsByUuid = new Map<string, StoragePhoto>();
  for (const row of serverRows) {
    const prev = rowsByUuid.get(row.uuid);
    if (
      !prev ||
      (row.status === FileStatus.ACTIVE && prev.status !== row.status)
    ) {
      rowsByUuid.set(row.uuid, row);
    }
  }

  for (const file of Array.from(rowsByUuid.values())) {
    seen.add(file.uuid);
    const upload = queueByUuid.get(file.uuid);
    const finished =
      file.status === FileStatus.ACTIVE && upload?.phase === "complete";
    entries.push({
      uuid: file.uuid,
      file,
      upload: upload && !finished ? upload : undefined,
    });
  }

  for (const upload of queueItems) {
    if (seen.has(upload.uuid)) continue;
    seen.add(upload.uuid);
    entries.push({ uuid: upload.uuid, upload });
  }

  return entries;
}
